<template>
  <app-layout title="Profile">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        模試情報管理
      </h2>
    </template>
    <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-3 flex-col">
          <div>
            <examination-menu
              :examinations="examinations"
              :selected-examination="examination" />
          </div>
        </div>
        <div class="col-span-9">
          <div class="flex justify-end">
            <button
              :disabled="!pagination.data.length"
              class="px-8 py-2 rounded bg-green-500 text-red-50 max-w-max shadow-sm hover:shadow-lg mr-4 disabled:bg-gray-400 disabled:cursor-not-allowed"
              @click.prevent="onCsvDownloadButtonClicked">
              CSVダウンロード
            </button>
            <button
              :disabled="isExpired"
              class="px-8 py-2 rounded bg-red-600 text-red-50 max-w-max shadow-sm hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
              @click.prevent="show = true">
              問題解除
            </button>
          </div>
          <div class="grid grid-cols-3 flex py-2 text-gray-600">
            <div class="col-span-3">
              PerPage:
              <select
                :value="paginatePerPage"
                class="ml-2 pl-2 rounded border-gray-400"
                @change="onChangedPerPage">
                <option
                  v-for="(n, i) in paginatePerPages"
                  :key="i">
                  {{ n }}
                </option>
              </select>
            </div>
          </div>
          <div class="grid grid-cols-12 flex mb-4">
            <input
              v-model="form.subjectKeyword"
              type="search"
              class="col-span-2 form-input border border-gray-300 outline-none rounded-md py-2 px-4 bg-white placeholder-gray-400 text-gray-500 appearance-none w-full block focus:outline-none"
              placeholder="分野" />
            <input
              v-model="form.questionKeyword"
              type="search"
              class="col-span-5 ml-2 form-input border border-gray-300 outline-none rounded-md py-2 px-4 bg-white placeholder-gray-400 text-gray-500 appearance-none w-full block focus:outline-none"
              placeholder="問題文" />
            <input
              v-model="form.correctAnswerKeyword"
              type="search"
              class="col-span-2 ml-4 form-input border border-gray-300 outline-none rounded-md py-2 px-4 bg-white placeholder-gray-400 text-gray-500 appearance-none w-full block focus:outline-none"
              placeholder="正答" />
            <button
              class="col-span-1 relative ml-6 px-6 py-2 rounded bg-blue-600 text-blue-50 max-w-max shadow-sm hover:shadow-lg"
              @click.prevent="onSearchButtonClicked">
              <svg
                class="absolute pointer-events-none w-6 h-6 top-1/4 left-1/4 text-white"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24">
                <path
                  d="M16.32 14.9l5.39 5.4a1 1 0 0 1-1.42 1.4l-5.38-5.38a8 8 0 1 1 1.41-1.41zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z" />
              </svg>
            </button>
            <button
              class="col-span-2 bg-white hover:bg-gray-100 text-black ml-4 py-2 border border-gray-200 rounded shadow"
              @click.prevent="onResetButtonClicked">
              リセット
            </button>
          </div>
          <question-list
            :pagination="pagination"
            :subject="subject"
            :examination="examination"
            :current-examination-type="currentExaminationType"
            :subject-names="props.subjectNames"
            :limit="limit"
            :is-expired="isExpired"
            @change-tab="changedTab"
            @select-questions="state.selectedQuestions = $event"
            @initialize-questions="initializeQuestions" />
        </div>
      </div>
    </div>
    <dialog-modal
      :show="show"
      @close="show = !show">
      <template #title>
        <div class="flex">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div class="ml-2 text-red-500">選択した問題を模試から解除してもよろしいですか？</div>
        </div>
      </template>
      <template #content>
        <div v-if="state.selectedQuestions && state.selectedQuestions.length">
          <p class="my-1 py-2">選択済み問題リスト</p>
          <div
            v-for="(item, index) in state.selectedQuestions"
            :key="item?.id ?? index"
            class="relative grid grid-cols-1 gap-4 p-4 mb-4 border rounded-lg bg-white shadow-lg">
            <template v-if="item?.id ?? false">
              <div class="relative flex gap-4">
                <div class="flex flex-col w-full">
                  <div class="flex flex-row justify-between">
                    <p class="relative text-xl whitespace-nowrap truncate overflow-hidden">問題ID {{ item.id }}</p>
                  </div>
                  <p class="text-gray-500 text-sm">科目名 {{ subjectName(item.subject_id) }}</p>
                </div>
              </div>
              <p class="-mt-4 text-gray-500">問題文 {{ item.question }}</p>
            </template>
          </div>
        </div>
        <div v-else>
          問題が選択されていません。
        </div>
      </template>
      <template #footer>
        <white-button
          class="mr-4 bg-white text-black"
          @click="show = !show">
          キャンセル
        </white-button>
        <button
          :disabled="!state.selectedQuestions.length"
          :class="[state.selectedQuestions.length ? 'bg-red-500 text-blue-50' : 'bg-gray-400 text-white cursor-not-allowed']"
          class="px-8 py-2 rounded max-w-max shadow-sm hover:shadow-lg"
          @click="onLiftQuestionButtonClicked(state.selectedQuestions)">
          解除
        </button>
      </template>
    </dialog-modal>
  </app-layout>
</template>

<script setup>
import { Inertia } from '@inertiajs/inertia'
import {
  reactive, ref, onMounted,
} from 'vue'
import moment from 'moment'
import AppLayout from '@/Layouts/AppLayout'
import ExaminationMenu from './Partials/ExaminationMenu'
import QuestionList from './Partials/QuestionList'
import DialogModal from '../../Common/DialogModal'
import WhiteButton from '../../Common/Button'

const props = defineProps({
  examinations: {
    type: Array,
    default: () => [],
  },
  examination: {
    type: Object,
    default: () => {
    },
  },
  pagination: {
    type: Object,
    default: () => {
    },
  },
  subject: {
    type: Object,
    default: () => {
    },
  },
  subjectKeyword: {
    type: String,
    default: null,
  },
  questionKeyword: {
    type: String,
    default: null,
  },
  correctAnswerKeyword: {
    type: String,
    default: null,
  },
  limit: {
    type: String,
    default: '10',
  },
  subjectNames: {
    type: Object,
    default: () => {},
  },
  currentExaminationType: {
    type: Number,
    default: 1,
  },
})

const form = reactive(Inertia.form({
  subjectKeyword: props.subjectKeyword,
  questionKeyword: props.questionKeyword,
  correctAnswerKeyword: props.correctAnswerKeyword,
}))

const subjectName = (subjectId) => props.subjectNames.find((item) => item.id === subjectId).name

const isExpired = ref(false)
const paginatePerPage = ref('15')
const paginatePerPages = ['5', '10', '15', '30', '50', '100']
const onChangedPerPage = (e) => {
  Inertia.get(route('examinations', {
    examination: props.examination,
    limit: e.target.value,
    examinationType: props.currentExaminationType,
  }, {
    only: ['pagination'],
  }))
}

const onSearchButtonClicked = () => {
  Inertia.get(route('examinations', {
    subjectKeyword: form.subjectKeyword,
    questionKeyword: form.questionKeyword,
    correctAnswerKeyword: form.correctAnswerKeyword,
    examination: props.examination,
    limit: props.limit,
    examinationType: props.currentExaminationType,
  }, {
    only: ['pagination'],
  }))
}

const changedTab = (type) => {
  Inertia.get(route('examinations', {
    examination: props.examination,
    limit: props.limit,
    examinationType: type,
  }, {
    only: ['pagination'],
  }))
}

const onResetButtonClicked = () => {
  form.subjectKeyword = ''
  form.questionKeyword = ''
  form.correctAnswerKeyword = ''
  Inertia.get(route('examinations', {
    examination: props.examination,
    limit: props.limit,
    examinationType: props.currentExaminationType,
  }, {
    only: ['pagination'],
  }))
}

const show = ref(false)
const state = reactive({
  selectedQuestions: [],
  selectedExaminations: [],
  initializeQuestions: {},
})
const initializeQuestions = (childInitializeMethod) => {
  state.initializeQuestions = childInitializeMethod
}
const onLiftQuestionButtonClicked = (questions) => {
  const questionIds = questions.filter((item) => !!item).map((item) => item.id)
  Inertia.post(route('examinations.liftQuestion', {
    examination: props.examination.id,
    questionIds,
    examinationType: props.currentExaminationType,
    limit: props.limit,
    page: props.pagination.current_page,
  }))
  state.initializeQuestions()
  show.value = false
}

const onCsvDownloadButtonClicked = () => {
  window.location.href = route('examination.downloadCsv', {
    examinationId: props.examination.id,
    examinationType: props.currentExaminationType,
  })
}

onMounted(() => {
  paginatePerPage.value = props.pagination.per_page

  const now = moment()
  const start = moment(props.examination.start_date)
  if (now.isAfter(start)) {
    isExpired.value = true
  }
})
</script>
